import api from "../../services/baseapi";

export type TicketType = "team_support" | "technical_support";
export type TicketStatus = "waiting_for_response" | "in_progress" | "resolved";

// ── Ticket ────────────────────────────────────────────────────────────────────

export interface TicketResponse {
  id: string;
  project_id: string;
  email: string | null;
  ticket_type: TicketType;
  subject: string | null;
  description: string;
  status: TicketStatus;
  created_by: number;
  created_by_username: string;
  resolved_by: number | null;
  resolved_by_username: string | null;
  resolved_at: string | null;
  created_at: string;
  modified_at: string | null;
  messages_count: number;
  attachments_count: number;
}

export interface TicketsListResponse {
  total: number;
  skip: number;
  limit: number;
  counts_by_ticket_type: {
    team_support: number;
    technical_support: number;
  };
  tickets: TicketResponse[];
}

export interface GetTicketsParams {
  ticket_type?: TicketType;
  status?: TicketStatus;
  search?: string;
  skip?: number;
  limit?: number;
}

export interface CreateTicketPayload {
  email?: string;
  ticket_type?: TicketType;
  subject?: string;
  description: string;
}

// ── Messages ──────────────────────────────────────────────────────────────────

export interface MessageAttachment {
  id: string;
  ticket_id: string;
  message_id: string | null;
  file_name: string;
  url: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by: number;
  created_at: string;
}

export interface MessageResponse {
  id: string;
  ticket_id: string;
  user_id: number | null;     // null = support team email reply
  username: string | null;    // null = support team email reply
  content: string;
  created_at: string;
  modified_at: string | null;
  attachments: MessageAttachment[];
}

export interface MessagesListResponse {
  total: number;
  messages: MessageResponse[];
}

// ── AI Assistant ──────────────────────────────────────────────────────────────

export interface AIMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AIConversation {
  id: string;
  title: string | null;
  category: string | null;
  issue: string | null;
  ticket_id: string | null;
  created_at: string;
  last_message_at: string;
  expires_at: string;
}

export interface AIConversationResponse {
  conversation: AIConversation | null;
  messages: (AIMessage & { id: string; sources: string[] | null; created_at: string })[];
}

export interface AIAskPayload {
  messages: AIMessage[];
  category?: string;
  issue?: string;
  conversation_id?: string;
}

export interface AIAskResponse {
  answer: string;
  action: "answer" | "escalate";
  sources: string[] | null;
  ticket_draft: { ticket_type: string; subject: string; description: string } | null;
  ai_included: boolean;
  conversation_id: string;
}

export interface AIEscalatePayload {
  project_id?: string;
  conversation_id?: string;
  category?: string;
  issue?: string;
  subject?: string;
  description?: string;
  email?: string;
  messages?: AIMessage[];
}

// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ── Tickets ───────────────────────────────────────────────────────────────────

export const getTickets = async (
  params: GetTicketsParams,
): Promise<TicketsListResponse> => {
  const { data } = await api.get<TicketsListResponse>("/help-center/tickets", { params });
  return data;
};

export const getTicket = async (ticketId: string): Promise<TicketResponse> => {
  const { data } = await api.get<TicketResponse>(`/help-center/tickets/${ticketId}`);
  return data;
};

export const createTicket = async (
  payload: CreateTicketPayload,
): Promise<TicketResponse> => {
  const { data } = await api.post<TicketResponse>("/help-center/tickets", payload);
  return data;
};

export const deleteTicket = async (ticketId: string): Promise<void> => {
  await api.delete(`/help-center/tickets/${ticketId}`);
};

export const changeTicketStatus = async (
  ticketId: string,
  status: TicketStatus,
): Promise<TicketResponse> => {
  const { data } = await api.patch<TicketResponse>(
    `/help-center/tickets/${ticketId}/status`,
    { status },
  );
  return data;
};

// ── Messages ──────────────────────────────────────────────────────────────────

export const getMessages = async (
  ticketId: string,
): Promise<MessageResponse[]> => {
  const { data } = await api.get(`/help-center/tickets/${ticketId}/messages`);
  // API returns { total, messages } shape
  if (data?.messages && Array.isArray(data.messages)) return data.messages;
  if (Array.isArray(data)) return data;
  return [];
};

export const sendMessage = async (
  ticketId: string,
  content: string,
  files?: File[],
): Promise<MessageResponse> => {
  const fd = new FormData();
  if (content.trim()) fd.append("content", content.trim());
  files?.forEach((f) => fd.append("files", f));
  const { data } = await api.post<MessageResponse>(
    `/help-center/tickets/${ticketId}/messages`,
    fd,
  );
  return data;
};

// ── Attachments ───────────────────────────────────────────────────────────────

export const downloadAttachment = async (
  ticketId: string,
  attachmentId: string,
  fileName: string,
): Promise<void> => {
  const { data } = await api.get(
    `/help-center/tickets/${ticketId}/attachments/${attachmentId}/download`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadAllMessageAttachments = async (
  ticketId: string,
  messageId: string,
): Promise<void> => {
  const { data } = await api.get(
    `/help-center/tickets/${ticketId}/messages/${messageId}/attachments/download`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "attachments.zip";
  a.click();
  window.URL.revokeObjectURL(url);
};

// ── AI Assistant ──────────────────────────────────────────────────────────────

export const loadAIConversation = async (): Promise<AIConversationResponse> => {
  const { data } = await api.get<AIConversationResponse>(
    "/help-center/assistant/conversation",
  );
  return data;
};

export const askAI = async (
  payload: AIAskPayload,
): Promise<AIAskResponse> => {
  const { data } = await api.post<AIAskResponse>(
    "/help-center/assistant/ask",
    payload,
  );
  return data;
};

export const escalateToTicket = async (
  payload: AIEscalatePayload,
): Promise<TicketResponse> => {
  const { data } = await api.post<TicketResponse>(
    "/help-center/assistant/escalate",
    payload,
  );
  return data;
};

export { PAGE_SIZE };
